import { useState, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { constants } from 'appConstants';
import { motion } from 'framer-motion';
import {
  ChatPageSidebar,
  ChatPageSidebarMobile,
  ChatPageContainer,
  ChatPageSidebarContainer,
  ChatPageMessagesContainer,
  BrandLogoChat,
  ChatAIInput,
  ChatMenuStack,
  MenuIcon,
  CHATAI_LOADING_STATUS,
  useChatContext,
} from '@ltvco/refresh-lib/v2';
import { chatSidebarAccessTags, sidebarNavItems } from '../../constants';

interface ChatPageLayoutProps {
  children: ReactNode;
  /** Whether to show the ChatAI input at the bottom (mobile) */
  showChatInput?: boolean;
}

export const ChatPageLayout = ({
  children,
  showChatInput = false,
}: ChatPageLayoutProps) => {
  const {
    messages,
    loadingStatus,
    showLanding,
    isLogoAnimating,
    logoAnimationPhase,
    hasCompletedAnimation,
    isReportOpen,
    triggerReportClose,
    handleNewChat,
    isPhotoDamageFeature,
    isValueForecasterFeature,
  } = useChatContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const { isMobile, isMedium } = useScreenSize();

  const reportLibraryRoute = `${constants.links.chatAILink}/library`;
  const isReportLibraryOpen = location.pathname === reportLibraryRoute;
  const shouldShowHeaderMenu = isMedium && !isReportOpen;

  const headerLogoVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  const closeSidebarMenu = useCallback(() => {
    if (!isSidebarOpen) {
      return;
    }
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsSidebarOpen(false);
      setIsAnimatingOut(false);
    }, 300);
  }, [isSidebarOpen]);

  const handleMenuClick = () => {
    if (isSidebarOpen) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsSidebarOpen(false);
        setIsAnimatingOut(false);
      }, 300);
    } else {
      setIsSidebarOpen(true);
    }
  };

  const handleReportLibraryClick = useCallback(() => {
    navigate(reportLibraryRoute);
    closeSidebarMenu();
  }, [navigate, reportLibraryRoute, closeSidebarMenu]);

  const handleCloseReportLibrary = useCallback(() => {
    navigate(constants.links.chatAILink);
    triggerReportClose();
  }, [navigate, triggerReportClose]);

  const handleNewChatClick = useCallback(() => {
    closeSidebarMenu();
    handleNewChat();
    handleCloseReportLibrary();
  }, [
    closeSidebarMenu,
    triggerReportClose,
    navigate,
    handleNewChat,
    handleCloseReportLibrary,
  ]);

  const repairCostEstimatorRoute = `${constants.links.chatAILink}/repair-cost-estimator`;
  const isRepairCostEstimatorRoute =
    location.pathname === repairCostEstimatorRoute;

  const shouldShowChatInput =
    (showChatInput &&
      !isReportLibraryOpen &&
      isMobile &&
      !showLanding &&
      messages.length > 0) ||
    ((isPhotoDamageFeature ||
      isValueForecasterFeature ||
      isRepairCostEstimatorRoute) &&
      isMobile);

  return (
    <ChatPageContainer>
      {!isMedium ? (
        <ChatPageSidebarContainer>
          <ChatPageSidebar
            onReportLibraryClick={handleReportLibraryClick}
            additionalSidebarItems={sidebarNavItems}
            sidebarAccessTags={chatSidebarAccessTags}
            onNewChatClick={handleNewChatClick}
          />
        </ChatPageSidebarContainer>
      ) : (
        <>
          {shouldShowHeaderMenu && (
            <ChatMenuStack>
              <motion.div
                variants={headerLogoVariants}
                initial="hidden"
                animate={hasCompletedAnimation ? 'visible' : 'hidden'}
              >
                <BrandLogoChat
                  width="116px"
                  height="18px"
                  sx={{
                    transform: 'translateY(0) translateX(0) scale(0.7)',
                  }}
                />
              </motion.div>
              <MenuIcon onClick={handleMenuClick} />
            </ChatMenuStack>
          )}
          {(isSidebarOpen || isAnimatingOut) && (
            <ChatPageSidebarMobile
              isOpen={isSidebarOpen && !isAnimatingOut}
              setIsOpen={setIsSidebarOpen}
              onReportLibraryClick={handleReportLibraryClick}
              onNewChatClick={handleNewChatClick}
              additionalSidebarItems={sidebarNavItems}
              sidebarAccessTags={chatSidebarAccessTags}
            />
          )}
        </>
      )}
      <ChatPageMessagesContainer isLanding={showLanding}>
        {children}
      </ChatPageMessagesContainer>
      {shouldShowChatInput && (
        <ChatAIInput
          isLanding={false}
          disabled={messages.length === 0}
          isAnimating={isLogoAnimating}
          animationPhase={logoAnimationPhase}
          isLoading={
            loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message
          }
        />
      )}
    </ChatPageContainer>
  );
};
